import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  errorToast,
  getFilterKey,
  getLocalStorageItem,
  successToast,
  useOutsideClick
} from '../../../../../utils/helper';
import { Api } from '../../../../../api';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import SelectMenu from '../../../../../component/common/SelectMenu';
import SearchInput from '../../../../../component/common/Input/SearchInput';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import {
  PER_PAGE,
  STATUS_FOR_DROPDOWN,
  USER_TYPE,
  USER_TYPE_B2B
} from '../../../../../utils/constants';
import Table from '../../../../../component/common/Table';
import Pagination from '../../../../../component/common/Pagination/Pagination';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import { PartnersPageData } from '../../../../../utils/pageContent';
import { FilterPanel, TableCard} from '../../../../../component/common/designSystem';

function PartnerList({ topbarShow = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const hasInitRef = useRef(false);
  const lastSearchAppliedRef = useRef('');
  const suppressURLSyncRef = useRef(true);
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const pages = [
    {
      name: 'Partners',
      href: '/partners',
      current: true
    }
  ];

  const redirectUrl = userData?.slug ? `/partners/add-partner` : '/partners/add-partner';

  // Initialize state from URL params synchronously to prevent reset on back navigation
  const getInitialStateFromURL = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get('search') || '',
      sortBy: params.get('sortBy') || '',
      sortOrder: parseInt(params.get('sortOrder')) || -1,
      perPage: parseInt(params.get('perPage')) || 10,
      page: parseInt(params.get('page')) || 1,
      filters: params.get('filters') || ''
    };
  };

  const initialState = getInitialStateFromURL();

  const [loader, setLoader] = useState(true);
  const [adminList, setAdminList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [selectedPerPage, setSelectedPerPage] = useState(
    initialState.perPage
      ? { label: String(initialState.perPage), value: initialState.perPage }
      : PER_PAGE[0]
  );
  const [searchTerm, setSearchTerm] = useState(initialState.search);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterQuery, setFilterQuery] = useState(initialState.filters);

  // Parse initial filter criteria from URL
  const parseInitialFilters = (filtersStr) => {
    if (!filtersStr) return { userType: '', accountStatus: '' };
    const cleaned = filtersStr.startsWith('&') ? filtersStr.slice(1) : filtersStr;
    const entries = cleaned
      .split('&')
      .filter(Boolean)
      .map((pair) => pair.split('='));
    const criteria = { userType: '', accountStatus: '' };
    const mapValueToOption = (value, options) => {
      const num = isNaN(Number(value)) ? value : Number(value);
      return (
        options.find((o) => o.value === num) || options.find((o) => o.value === value) || value
      );
    };
    entries.forEach(([key, val]) => {
      if (!key) return;
      if (key === 'userType') {
        criteria.userType = mapValueToOption(val, USER_TYPE_B2B);
      } else if (key === 'accountStatus') {
        criteria.accountStatus = mapValueToOption(val, STATUS_FOR_DROPDOWN);
      }
    });
    return criteria;
  };

  const [filterCriteria, setFilterCriteria] = useState(() =>
    parseInitialFilters(initialState.filters)
  );

  const handlePagination = (pageNumber, pageSize, searchKey, query, sortBy, sortOrder) => {
    setLoader(true);
    Api.getPartners(pageNumber, pageSize, searchKey, query, sortBy, sortOrder).then((response) => {
      if (response?.data?.meta?.code === 1) {
        setCurrentPage(pageNumber);
        setAdminList(response?.data?.data);
        setTotalCount(response?.data?.meta?.totalRecords);
        setLoader(false);
      } else if (response?.code === 401) {
        setLoader(false);
        errorToast(response?.message);
      } else if (response?.data?.meta?.code === 0) {
        setCurrentPage(1);
        setAdminList([]);
        setTotalCount(0);
        setLoader(false);
        errorToast(response?.data?.meta?.message);
      } else {
        setLoader(false);
      }
    });
  };

  // Query param helpers
  const updateURLParams = ({ search, sortBy, sortOrder, perPage, page, filters }) => {
    const params = new URLSearchParams(location.search);
    if (search !== undefined) params.set('search', search);
    if (sortBy !== undefined) params.set('sortBy', sortBy || '');
    if (sortOrder !== undefined) params.set('sortOrder', String(sortOrder));
    if (perPage !== undefined) params.set('perPage', String(perPage));
    if (page !== undefined) params.set('page', String(page));
    if (filters !== undefined) params.set('filters', filters || '');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
  };

  const initFromURLParams = () => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    const urlSortBy = params.get('sortBy') || '';
    const urlSortOrder = parseInt(params.get('sortOrder') || sortOrder || -1);
    const urlPerPage = parseInt(params.get('perPage') || selectedPerPage?.value || 10);
    const urlPage = parseInt(params.get('page') || 1);
    const urlFilters = params.get('filters') || '';
    if (urlSearch !== searchTerm) setSearchTerm(urlSearch);
    if (urlSortBy) setSortBy(urlSortBy);
    if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);
    if (urlPerPage !== selectedPerPage?.value)
      setSelectedPerPage({ label: String(urlPerPage), value: urlPerPage });
    if (urlFilters !== filterQuery) setFilterQuery(urlFilters);
    // Hydrate visual filter selections from filters query
    const parsedCriteria = parseFiltersToCriteria(urlFilters);
    if (parsedCriteria) setFilterCriteria(parsedCriteria);
    setCurrentPage(urlPage);

    handlePagination(urlPage, urlPerPage, urlSearch, urlFilters, urlSortBy || sortBy, urlSortOrder);
  };

  // Convert filters query string like "&userType=5&accountStatus=1" to filterCriteria
  const parseFiltersToCriteria = (filtersStr) => {
    if (!filtersStr) return { userType: '', accountStatus: '' };
    const cleaned = filtersStr.startsWith('&') ? filtersStr.slice(1) : filtersStr;
    const entries = cleaned
      .split('&')
      .filter(Boolean)
      .map((pair) => pair.split('='));
    const mapValueToOption = (value, options) => {
      const num = isNaN(Number(value)) ? value : Number(value);
      return (
        options.find((o) => o.value === num) || options.find((o) => o.value === value) || value
      );
    };
    const criteria = { userType: '', accountStatus: '' };
    entries.forEach(([key, val]) => {
      if (!key) return;
      switch (key) {
        case 'userType':
          criteria.userType = mapValueToOption(val, USER_TYPE_B2B);
          break;
        case 'accountStatus':
          criteria.accountStatus = mapValueToOption(val, STATUS_FOR_DROPDOWN);
          break;
        default:
          break;
      }
    });
    return criteria;
  };

  useEffect(() => {
    // Avoid duplicate init in StrictMode; only act if search actually changed
    if (location.search === lastSearchAppliedRef.current && hasInitRef.current) return;
    initFromURLParams();
    lastSearchAppliedRef.current = location.search;
    if (!hasInitRef.current) {
      hasInitRef.current = true;
      // Enable URL syncing after first hydration
      suppressURLSyncRef.current = false;
    }
    // Re-init when location.search changes via back/forward
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (searchTerm) {
      let searchParam = searchTerm.trim();
      const delayDebounceFn = setTimeout(() => {
        if (!suppressURLSyncRef.current) {
          updateURLParams({
            page: 1,
            perPage: selectedPerPage?.value,
            search: searchParam,
            sortBy,
            sortOrder,
            filters: filterQuery
          });
        }
        handlePagination(1, selectedPerPage?.value, searchParam, filterQuery, sortBy, sortOrder);
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    } else if (hasInitRef.current) {
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: 1,
          perPage: 10,
          search: '',
          sortBy,
          sortOrder,
          filters: filterQuery
        });
      }
      handlePagination(1, 10, '', filterQuery, sortBy, sortOrder);
    }
  }, [searchTerm]);

  const deleteHandler = (id) => {
    setLoader(true);
    setSearchTerm('');

    Api.deleteAdmin(id).then((response) => {
      if (response?.data?.meta?.code === 1) {
        handlePagination(1, selectedPerPage?.value, '', filterQuery, sortBy, sortOrder);
        successToast(response?.data?.meta?.message);
      } else if (response?.data?.meta?.code === 0) {
        setLoader(false);
        errorToast(response?.data?.meta?.message);
      } else {
        setLoader(false);
      }
    });
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: perPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: filterQuery
      });
    }
    handlePagination(1, perPage.value, searchTerm, filterQuery, sortBy, sortOrder);
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  const filterHandler = (data, key) => {
    setFilterCriteria((prevData) => ({
      ...prevData,
      [key]: data
    }));
    let clone = JSON.parse(JSON.stringify(filterCriteria));
    const newData = { ...clone, [key]: data };
    let tempFilterQuery = [];
    Object.keys(newData).forEach(function (key) {
      if (newData[key]?.value?.toString()) {
        tempFilterQuery.push(`&${key}=${newData[key]?.value}`);
      }
    });
    let query = tempFilterQuery?.toString()?.replaceAll(',', '');
    setFilterQuery(query);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: selectedPerPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: query
      });
    }
    handlePagination(1, selectedPerPage.value, searchTerm, query, sortBy, sortOrder);
  };

  const resetHandler = () => {
    setFilterCriteria({
      accountStatus: ''
    });
    setFilterQuery('');
    setShowFilterModal(false);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: selectedPerPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: ''
      });
    }
    handlePagination(1, selectedPerPage.value, searchTerm, '', sortBy, sortOrder);
  };

  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      let tempSortOrder = sortOrder === 1 ? -1 : 1;
      setSortOrder(tempSortOrder);
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: currentPage,
          perPage: selectedPerPage?.value,
          search: searchTerm,
          sortBy: sortByValue,
          sortOrder: tempSortOrder,
          filters: filterQuery
        });
      }
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        tempSortOrder
      );
    } else {
      setSortOrder(1);
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: currentPage,
          perPage: selectedPerPage?.value,
          search: searchTerm,
          sortBy: sortByValue,
          sortOrder: 1,
          filters: filterQuery
        });
      }
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        1
      );
    }
  };

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{PartnersPageData.metaTitle}</title>
      </Helmet>
      {loader ? <Loader /> : null}
      {topbarShow === true && <Topbar title={PartnersPageData.topbarTitleText} />}
      <Breadcrumb pageList={pages} className="lg:mt-8" />
      <div className="mt-6">
        <div className="sm:flex justify-between">
          <div className="flex">
            <div className="hidden sm:flex mr-3 self-center">
              <p className="self-center text-sm mr-2 whitespace-nowrap dark:text-white m-0">
                {PartnersPageData.paginationSupportText}
              </p>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={PartnersPageData.searchPlaceholder}
              />
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex justify-between">
            <div className="sm:hidden flex self-center">
              <span className="self-center text-sm mr-2 dark:text-white sm:block hidden whitespace-nowrap m-0">
                {PartnersPageData.paginationSupportText}
              </span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="flex">
              <div className="ml-3 self-center">
                <button
                  type="button"
                  onClick={() => navigate(redirectUrl)}
                  className="btn-primary sm:w-auto"
                >
                  {PartnersPageData.addNewPartnerBtnText}
                </button>
              </div>
              <div className="ml-3 self-center">
                <button
                  className="btn-primary sm:w-auto"
                  onClick={() => setShowFilterModal((state) => !state)}
                >
                  <FunnelIcon className="text-white w-[18px] h-[20px] inline" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <FilterPanel
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onReset={resetHandler}
        >
              <div>
                <SelectMenu
                  menuList={STATUS_FOR_DROPDOWN}
                  label={PartnersPageData.filtersModal.selectMenuOne.label}
                  isRequired={false}
                  defaultSelected={filterCriteria.accountStatus}
                  setSelectedMenu={(data) => filterHandler(data, 'accountStatus')}
                />
              </div>
            </FilterPanel>

        
        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) => {
              if (!suppressURLSyncRef.current) {
                updateURLParams({
                  page,
                  perPage: selectedPerPage?.value,
                  search: searchTerm,
                  sortBy,
                  sortOrder,
                  filters: filterQuery
                });
              }
              handlePagination(
                page,
                selectedPerPage?.value,
                searchTerm,
                filterQuery,
                sortBy,
                sortOrder
              );
            },
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={PartnersPageData.tableColumns}
            data={adminList}
            setDeleteId={deleteHandler}
            name={'admin_table'}
            // addNewURL="/sub-admins/add-edit"
            addNewURL={redirectUrl}
            bottomBorder={totalCount > selectedPerPage?.value}
            setSortBy={(sort) => handleSortBy(sort)}
            loader={loader}
            setSearchTerm={(data) => setSearchTerm(data)}
          />
        </TableCard>
      </div>
      
    </div>
  );
}

export default PartnerList;
